/** Wire protocol between client and server. */
import type { DiscStateName, MatchKind, MatchPhase, PlayerStateName, RoundResult, SimState, Team } from '../sim/types.js'
import type { SimEvent } from './events.js'
import type { AccoladeId } from './accolades.js'
import type { InputWire } from './inputcodec.js'

export const PROTOCOL_VERSION = 2

export type RoomKind = 'hub' | 'match'

// ── Client → Server ─────────────────────────────────────────────────────────

export interface HelloMsg {
  v: number
  token?: string
  name?: string
  spectate?: string
}

export interface InputMsg {
  /** Last INPUT_REDUNDANCY records, ascending seq (05 §1). */
  inputs: InputWire[]
}

export interface PingMsg {
  t: number
  ack?: { s: number; hold: number }
}

export interface QueueMsg { want: boolean }

// 06 §4 lobby
export interface LobbyPickMsg { ids: string[] }
export interface LobbyLockMsg { id: string }

// 10 §2 / 04 §10 / 07 §7
export interface RematchMsg { want: boolean }
export interface VsBotMsg { tier?: 'ROOKIE' | 'PRO' | 'ACE' }
export interface SetNameMsg { name: string }
// 12 §4 runs (Sprint 9): start from the hub kiosk; pick a run card; the server narrates the sequence.
export interface RunStartMsg { tier?: number }
export interface RunCardMsg { id: string }
export type RunStage = 'RUN_START' | 'BOUT' | 'CARD' | 'WARDEN' | 'RESULTS'
export interface RunStateMsg {
  runId: string
  stage: RunStage
  /** Current/last bout index 1..3 (0 before the first). */
  bout: number
  tier: number
  /** This member's picked run cards so far and the three on offer (RUN_START / CARD). */
  cards: string[]
  offered: string[]
  /** ms left in the pick window (RUN_START / CARD) or in the run overall. */
  timeLeftMs: number
  outcome?: 'won' | 'failed' | 'abandoned'
  /** RESULTS: XP awarded to this member, whether a first clear granted an imprint token. */
  xp?: number
  firstClear?: boolean
  members: string[]
}

// 12 §7.4 device linking (Sprint 8): issue a code on this device, redeem it on another; both then share the account.
export interface LinkRedeemMsg { code: string }
export interface LinkCodeMsg { code: string; expiresAt: string }
export interface LinkResultMsg { ok: boolean; reason?: 'BAD_CODE' | 'EXPIRED' | 'SELF' }

// 12 §2 imprint slot intent (Sprint 8): slot 2 or 3 ← power-up id, or null to clear.
export interface SlotMsg { index: 2 | 3; id: string | null }
export interface SlotResultMsg { ok: boolean; index: 2 | 3; reason?: 'LOCKED_SLOT' | 'NOT_UNLOCKED' | 'INCOMPATIBLE' | 'BAD_ID' | 'NO_TOKENS'; keys?: string[]; /** Imprint tokens spent by this write (12 §2: re-slotting an occupied slot costs 1). */ tokensSpent?: number }

// 12 §1–§3 disclord view (Sprint 8): what the client shows and what the kiosk edits.
export interface DisclordView {
  level: number
  xp: number
  /** XP into the current level and XP needed for the next (0/0 at MAX_LEVEL). */
  into: number
  toNext: number
  imprintTokens: number
  /** Slots 1..3 by index (slot 1 is the match pick, filled in the lobby; 2–3 are imprints). null = empty/locked. */
  slots: (string | null)[]
  /** Number of slots open at this level (12 §2 SLOT_LEVELS). */
  slotsOpen: number
  /** Unlocked power-up ids (kit). */
  kit: string[]
}

// 10 §2 parties (reserved partyId, Sprint 7): join/create a 4-char code, or null to leave.
export interface PartyMsg { code: string | null }
export interface PartyStateMsg { code: string | null; members: string[]; error?: 'FULL' | 'BAD_CODE' }

// 13 §2: playtest marker stamped into the event log by the player.
export type FeedbackTag = 'unfair' | 'slow' | 'fast' | 'confusing'
export interface FeedbackMsg { tag: FeedbackTag }

// ── Server → Client ─────────────────────────────────────────────────────────

export interface WelcomeMsg {
  ok: true
  token: string
  playerId: string
  roomId: string
  roomKind: RoomKind
  team: Team | null
  spectator: boolean
  tickRate: number
  snapshotHz: number
  serverTime: number
  serverTick: number
  state: SimState
}

export interface ErrorMsg { ok: false; error: string }

export interface PongMsg { t: number; s: number; s2: number; tick: number }

export interface PlayerSnap {
  id: string
  name: string
  bot: 0 | 1
  abs: 0 | 1
  team: Team
  slot: number
  x: number; y: number; z: number
  vx: number; vy: number; vz: number // vy: airborne prediction re-integrates from it (S10 fix 2026-09-14)
  ax: number; az: number // aim
  st: PlayerStateName
  stt: number // stateTicks
  sta: number // stamina
  rgd: number // regenDelayTicks
  dcd: number // dashCooldownTicks
  slk: number // shieldLockoutTicks
  sag: number // shieldAgeTicks
  tax: number; taz: number // throwAim
  ddx: number; ddz: number // dashDir
  held: number[]
  max: number
  alive: 0 | 1
  inv: number // invulnUntilTick
  rsp: number // respawnTick
  seq: number
  pb: number // prevButtons bits (1 throw, 4 dash, 8 jump)
  g: 0 | 1 // grounded (spec §1.5)
  lt: number // landTicks
  pu: string[]
  /** 12 §2 imprints and 12 §8.2 slot count (Sprint 8). */
  imp: string[]
  sl: number
}

export interface DiscSnap {
  id: number
  ho: string // homeOwner
  hl: string | null // holder
  th: string | null // thrower
  team: Team
  st: DiscStateName
  stt: number
  x: number; y: number; z: number
  vx: number; vy: number; vz: number
  rl: number // rangeLeft
  bc: number // bounceCount
  hm: 0 | 1 // homing (reserved)
  dot: 0 | 1 // despawnOnTimeout
  lz: Team | null
}

/** 06 §4 visibility: decks are public from LOBBY_LOCK on; during LOBBY_LOCK
 *  locks show only LOCKED/CHOOSING; ids appear from REVEAL. */
export interface LobbySnap {
  decks: Record<string, string[]>
  locks: Record<string, 'CHOOSING' | 'LOCKED' | string>
  /** Ticks left in the current lobby phase. */
  ticksLeft: number
}

export interface MatchSnap {
  phase: MatchPhase
  phaseTicks: number
  lobby: LobbySnap | null
  roundResults: RoundResult[]
  roundNumber: number
  roundIndex: number
  score: [number, number]
  voidRounds: number
  timerTicks: number
  overtimeTicks: number
  bounds: SimState['match']['bounds']
  winner: Team | null
  paused: boolean
}

export interface SnapshotMsg {
  tick: number
  /** Server wall clock (ms) when taken. */
  t: number
  players: PlayerSnap[]
  discs: DiscSnap[]
  match: MatchSnap
  /** Events since the previous snapshot (includes server-only `rescind`). */
  events: SimEvent[]
}

export interface ResyncMsg { state: SimState; serverTime: number; serverTick: number }

export interface RoomInfo {
  id: string
  kind: RoomKind
  matchKind: MatchKind
  players: number
  spectators: number
  phase: MatchPhase
  score: [number, number]
  arenaId: string
}

export interface RoomsMsg { rooms: RoomInfo[] }

export interface JoinedMsg {
  roomId: string
  roomKind: RoomKind
  team: Team | null
  spectator: boolean
  serverTick: number
}

/** 10 §2 queue state for Z12. */
export interface QueueStateMsg {
  state: 'OFF' | 'FLAGGED' | 'PULLED'
  flagged: number
  /** ms until launch during the fill wait, or null. */
  launchInMs: number | null
  /** D11: the lone-flagged "vs BOT" offer is available. */
  vsBotAvailable: boolean
  /** D11 countdown: ms until the offer opens while flagged alone (0 once open), else null. */
  vsBotInMs: number | null
  /** 10 §2 parties: flagged, but the launch pick skips this player until the party partner flags in this hub. */
  waitingForParty?: boolean
}

/** 10 §4 kiosk data (render is a client placeholder). */
export interface KioskMsg {
  live: { id: string; teams: [string[], string[]]; score: [number, number]; round: number; timerTicks: number; phase: MatchPhase; spectators: number; full: boolean; kind: MatchKind; /** 12 §4: for run rooms, the bout index (1–3) or 'W' for the Warden. */ run?: string }[]
  lastResult: MatchResultMsg | null
  online: number
  inMatch: number
  inHub: number
  queue: { flagged: number; launchInMs: number | null }
  drain: { graceMs: number } | null
  mostPicked: { id: string; picks: number }[]
}

/** 10 §6 reconnect pause/resume. */
export interface PauseMsg { playerId: string; name: string; graceEndsInMs: number }
export interface ResumeMsg { inTicks: number }

export interface MatchResultMsg {
  roomId: string
  winner: Team | null
  score: [number, number]
  rounds: RoundResult[]
  coinUsed: boolean
  voidRounds: number
  players: { id: string; name: string; team: Team; stats: SimState['players'][number]['matchStats']; powerUps: string[]; accolade: string | null; bot: boolean }[]
  kind: MatchKind
  /** Players who have pressed REMATCH (04 §10). */
  rematch: string[]
  /** Attacker of the hit that ended the final counted round (04 §10 Closer), if any. */
  decidingPlayerId: string | null
  /** Server-derived per the 04 §10 ruling (shared/accolades.ts); the client never derives them. */
  accolades: Record<string, AccoladeId | null>
}

// 09 §5 training lane (client-scripted; the server only hosts the lane + TUTOR bot).
export interface TrainingMsg { want: boolean }
/** Step-script control of the TUTOR bot: stance + whether it may throw now. */
export interface TrainingBotMsg { throw?: boolean; stance?: 'idle' | 'stand' | 'hide' | 'volley' | 'sporadic' }
